import React, { Component } from 'react'
import styled from 'styled-components'
import {FAQ_SECTION_ORDER, getVisibleLifestyleFaqs} from "../utils/lifeStyleFaqs";

export const Section = styled.section`
  margin: 0 0 1.25rem 0;
`

export const Card = styled.div`
  background: #fff7ed;
  border: 2px solid var(--orange);
  border-radius: 10px;
  padding: 1rem 1.25rem;
`

export const SectionGroup = styled.div`
  & + & {
    margin-top: 1.5rem;
  }
`

export const SectionHeading = styled.h3`
  margin: 0 0 0.85rem 0;
  font-size: 1rem;
  font-weight: 700;
  color: #2f6fb3;
`

export const Item = styled.div`
  & + & {
    margin-top: 0.75rem;
  }
`

export const QuestionButton = styled.button`
  width: 100%;
  text-align: left;
  border: 2px solid ${props => (props.open ? 'var(--orange)' : '#ffd7aa')};
  background: ${props => (props.open ? '#fff' : '#fffaf5')};
  border-radius: 10px;
  padding: 0.85rem 1rem;
  cursor: pointer;
  font-size: 1rem;
  line-height: 150%;
`

export const Answer = styled.div`
  margin-top: 0.5rem;
  padding: 0.95rem 1rem;
  background: #fff;
  border-left: 4px solid var(--orange);
  border-radius: 8px;
`

export const AnswerBlock = styled.div`
  & + & {
    margin-top: 1rem;
  }
`

export const AnswerParagraph = styled.p`
  margin: 0;
  line-height: 175%;
`

export const EmphasizedAnswerParagraph = styled(AnswerParagraph)`
  font-size: 1.15rem;
`

export const AnswerHeading = styled.h4`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 150%;
`

export const BulletList = styled.ul`
  margin: 0 0 0 1.35rem;
  padding: 0;
`

export const EmphasizedBulletList = styled(BulletList)`
  font-size: 1.15rem;
`

export const BulletItem = styled.li`
  line-height: 175%;

  & + & {
    margin-top: 0.8rem;
  }
`

export const AnswerImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  margin: 0 auto;
  border: 1px solid var(--light-gray);
  border-radius: 10px;
`

export const InlineStrong = styled.strong`
  font-weight: 700;
`

export const InlineEmphasis = styled.em`
  font-style: italic;
`

export const InlineLink = styled.a`
  color: var(--orange);
  text-decoration: underline;
`

export const InlineCitation = styled.a`
  margin-left: 0.12rem;
  font-size: 0.72em;
  line-height: 1;
  vertical-align: super;
  color: #2f6fb3;
  text-decoration: underline;
  font-weight: 700;
`

const renderToken = (token, index) => {
  if (token.citation) {
    return (
      <InlineCitation
        key={index}
        href={token.href}
        target="_blank"
        rel="noopener noreferrer"
        title={token.title}
      >
        {token.text}
      </InlineCitation>
    )
  }

  let node = token.text

  if (token.href) {
    node = (
      <InlineLink
        href={token.href}
        target="_blank"
        rel="noopener noreferrer"
        title={token.title}
      >
        {node}
      </InlineLink>
    )
  }

  if (token.italic) {
    node = <InlineEmphasis>{node}</InlineEmphasis>
  }

  if (token.bold) {
    node = <InlineStrong>{node}</InlineStrong>
  }

  return <React.Fragment key={index}>{node}</React.Fragment>
}

const renderInlineContent = tokens =>
  tokens.map((token, index) => renderToken(token, index))

export function RichAnswer({ blocks, graphicVariant = 'A' }) {
  return (
    <div>
      {blocks.map((block, index) => {
        if (block.type === 'paragraph') {
          const ParagraphComponent = block.emphasis
            ? EmphasizedAnswerParagraph
            : AnswerParagraph

          return (
            <AnswerBlock key={index}>
              <ParagraphComponent>
                {renderInlineContent(block.content)}
              </ParagraphComponent>
            </AnswerBlock>
          )
        }

        if (block.type === 'heading') {
          return (
            <AnswerBlock key={index}>
              <AnswerHeading>
                {renderInlineContent(block.content)}
              </AnswerHeading>
            </AnswerBlock>
          )
        }

        if (block.type === 'bullets') {
          const ListComponent = block.emphasis
            ? EmphasizedBulletList
            : BulletList

          return (
            <AnswerBlock key={index}>
              <ListComponent>
                {block.items.map((item, itemIndex) => (
                  <BulletItem key={itemIndex}>
                    {renderInlineContent(item)}
                  </BulletItem>
                ))}
              </ListComponent>
            </AnswerBlock>
          )
        }

        if (block.type === 'image') {
          const image = block.variants
            ? block.variants[graphicVariant] || block.variants.A
            : block

          return (
            <AnswerBlock key={index}>
              <AnswerImage
                alt={image.alt || ''}
                src={image.src}
              />
            </AnswerBlock>
          )
        }

        return null
      })}
    </div>
  )
}

const groupFaqsBySection = faqs =>
  faqs.reduce((acc, faq) => {
    if (!acc[faq.section]) {
      acc[faq.section] = []
    }

    acc[faq.section].push(faq)
    return acc
  }, {})

export default class LifestyleFaqSection extends Component {
  state = {
    openFaqId: null,
  }

  toggleFaq = faqId => {
    this.setState(prevState => ({
      openFaqId: prevState.openFaqId === faqId ? null : faqId,
    }))
  }

  render() {
    const { diagnosis, gender } = this.props
    const { openFaqId } = this.state
    const faqs = getVisibleLifestyleFaqs(diagnosis, gender)
    const groupedFaqs = groupFaqsBySection(faqs)

    return (
      <Section>
        <Card>
          {FAQ_SECTION_ORDER.map(sectionTitle => {
            const sectionFaqs = groupedFaqs[sectionTitle]

            if (!sectionFaqs || sectionFaqs.length === 0) {
              return null
            }

            return (
              <SectionGroup key={sectionTitle}>
                <SectionHeading>{sectionTitle}</SectionHeading>

                {sectionFaqs.map(faq => {
                  const isOpen = openFaqId === faq.id

                  return (
                    <Item key={faq.id}>
                      <QuestionButton
                        type="button"
                        open={isOpen}
                        onClick={() => this.toggleFaq(faq.id)}
                      >
                        {faq.question}
                      </QuestionButton>

                      {isOpen && (
                        <Answer>
                          <RichAnswer blocks={faq.answer} />
                        </Answer>
                      )}
                    </Item>
                  )
                })}
              </SectionGroup>
            )
          })}
        </Card>
      </Section>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/LifeStyleFaqSection.js